import apiClient, { STATIC_CONFIG } from './api';

// 大学API服务
export const universityService = {
  /**
   * 获取大学详情
   * @param {string} universityId - 大学ID
   * @returns {Promise} 大学详情
   */
  getUniversityDetail: (universityId) => {
    return apiClient.get(`/api/universities/${universityId}`);
  },

  /**
   * 获取大学的院系列表
   * @param {string} universityId - 大学ID
   * @param {Object} params - 查询参数
   * @returns {Promise} 院系列表
   */
  getUniversityFaculties: (universityId, params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 50,
      ...params
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get(`/api/universities/${universityId}/faculties`, { params: filteredParams });
  },

  /**
   * 获取大学的专业列表
   * @param {string} universityId - 大学ID
   * @param {Object} params - 查询参数
   * @returns {Promise} 专业列表
   */
  getUniversityPrograms: (universityId, params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 20,
      ...params
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get(`/api/universities/${universityId}/programs`, { params: filteredParams });
  },

  /**
   * 获取大学的媒体资源（图片、视频）
   * @param {string} universityId - 大学ID
   * @param {string} type - 媒体类型 (images, videos, files)
   * @returns {Promise} 媒体资源列表
   */
  getUniversityMedia: (universityId, type = 'images') => {
    return apiClient.get(`/api/universities/${universityId}/media`, { 
      params: { type } 
    });
  },

  /**
   * 获取大学的文章内容
   * @param {string} universityId - 大学ID
   * @param {string} contentType - 内容类型 (intro, advantage, campus, dormitory, etc.)
   * @returns {Promise} 文章内容列表
   */
  getUniversityContent: (universityId, contentType = 'intro') => {
    return apiClient.get(`/api/universities/${universityId}/content`, { 
      params: { type: contentType } 
    });
  },

  /**
   * 获取大学的新闻文章
   * @param {string} universityId - 大学ID
   * @param {Object} params - 查询参数
   * @returns {Promise} 新闻文章列表
   */
  getUniversityNews: (universityId, params = {}) => {
    const queryParams = {
      page: params.page || 0,
      size: params.size || 10,
      ...params
    };

    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([key, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    return apiClient.get(`/api/universities/${universityId}/news`, { params: filteredParams });
  },

  /**
   * 获取大学统计数据
   * @param {string} universityId - 大学ID
   * @returns {Promise} 统计数据
   */
  getUniversityStats: (universityId) => {
    return apiClient.get(`/api/universities/${universityId}/stats`);
  }
};

// 大学数据转换工具函数
export const universityDataMapper = {
  /**
   * 构建完整的图片URL
   * @param {string} imagePath - 图片路径
   * @param {string} fallback - 默认图片路径
   * @param {string} universityName - 大学名称（用于生成占位头像）
   * @returns {string} 完整的图片URL
   */
  buildImageUrl: (imagePath, fallback = '/images/universities/default-logo.png', universityName = '') => {
    // 生成占位头像的辅助函数
    const generateAvatarUrl = (name) => {
      if (!name) return fallback;
      
      const config = STATIC_CONFIG.AVATAR_CONFIG;
      const encodedName = encodeURIComponent(name);
      return `${config.service}?name=${encodedName}&size=${config.size}&background=${config.background}&color=${config.color}&format=${config.format}&rounded=${config.rounded}&bold=${config.bold}`;
    };
    
    // 如果已经是完整URL，直接返回
    if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
      return imagePath;
    }
    
    // 如果有图片路径且启用了baseurl拼接
    if (imagePath && STATIC_CONFIG.ENABLE_IMAGE_BASEURL) {
      // 确保路径以/开头
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      const fullUrl = `${STATIC_CONFIG.IMAGE_BASE_URL}${cleanPath}`;
      
      return fullUrl;
    }
    
    // 如果没有图片路径或未启用baseurl拼接，使用占位头像
    if (universityName) {
      const avatarUrl = generateAvatarUrl(universityName);
      return avatarUrl;
    }
    
    return fallback;
  },

  /**
   * 将后端大学数据转换为前端格式
   * @param {Object} apiUniversity - 后端大学数据
   * @returns {Object} 前端大学数据格式
   */
  mapUniversityFromApi: (apiUniversity) => {
    // 解析成立年份
    const parseEstablishedYear = (establishedTime) => {
      if (!establishedTime) return '-';
      // 提取年份数字
      const yearMatch = establishedTime.toString().match(/(\d{4})/);
      return yearMatch ? parseInt(yearMatch[1]) : '-';
    };

    // 解析排名数据
    const parseRanking = (ranking) => {
      if (!ranking) return '-';
      const numRanking = parseInt(ranking);
      return isNaN(numRanking) ? '-' : numRanking;
    };

    // 解析学生数量
    const parseStudentCount = (count) => {
      if (!count) return '-';
      const numCount = parseInt(count);
      return isNaN(numCount) ? '-' : numCount.toLocaleString();
    };

    const universityName = apiUniversity.name_cn || apiUniversity.name_en || '';
    
    return {
      id: apiUniversity.id,
      // 用于SEO的英文名称连接（去空格及特殊符号后以连字符连接）
      name_en_concat: apiUniversity.name_en_concat || apiUniversity.slug || '',
      name: apiUniversity.name_cn || '',
      nameEn: apiUniversity.name_en || '',
      shorterForm: apiUniversity.shorter_form || '',
      logo: universityDataMapper.buildImageUrl(
        apiUniversity.logo, 
        '/images/universities/default-logo.png', 
        universityName
      ),
      isPublic: apiUniversity.is_public === 1,
      type: apiUniversity.is_public === 1 ? '公立大学' : '私立大学',
      established: parseEstablishedYear(apiUniversity.establish_time),
      ranking: {
        global: parseRanking(apiUniversity.ranking_global),
        asia: parseRanking(apiUniversity.ranking_asia)
      },
      leadership: {
        principal: apiUniversity.principal || '-',
        vicePrincipal: apiUniversity.vice_principal || '-'
      },
      students: {
        total: parseStudentCount(apiUniversity.number_of_students),
        undergraduate: parseStudentCount(apiUniversity.number_of_undergraduates),
        postgraduate: parseStudentCount(apiUniversity.number_of_postgraduates),
        international: parseStudentCount(apiUniversity.number_of_international_students)
      },
      teachers: parseStudentCount(apiUniversity.number_of_teachers),
      website: apiUniversity.official_website || '',
      mapLinks: {
        google: apiUniversity.google_map_link || '',
        baidu: apiUniversity.baidu_map_link || ''
      },
      languageScores: apiUniversity.acceptable_online_language_scores || '',
      campuses: apiUniversity.campuses || [],
      faculties: apiUniversity.faculties || [],
      programs: apiUniversity.programs || [],
      media: {
        images: apiUniversity.media_images || [],
        videos: apiUniversity.media_videos || [],
        files: apiUniversity.media_files || []
      },
      content: {
        intro: apiUniversity.intros || [],
        advantages: apiUniversity.advantages || [],
        campus: apiUniversity.campus_intros || [],
        dormitory: apiUniversity.dormitory_intros || [],
        admissionRequirements: {
          undergraduate: apiUniversity.undergraduate_admission_requirements || [],
          master: apiUniversity.master_admission_requirements || [],
          doctor: apiUniversity.doctor_admission_requirements || []
        }
      }
    };
  },

  /**
   * 将后端院系数据转换为前端格式
   * @param {Object} apiFaculty - 后端院系数据
   * @returns {Object} 前端院系数据格式
   */
  mapFacultyFromApi: (apiFaculty) => {
    return {
      id: apiFaculty.id,
      // 用于SEO的英文名称连接（去空格及特殊符号后以连字符连接）
      name_en_concat: apiFaculty.name_en_concat || apiFaculty.slug || '',
      name: apiFaculty.name_cn || '',
      nameEn: apiFaculty.name_en || '',
      director: apiFaculty.director || '-',
      teachers: apiFaculty.number_of_teachers || '-',
      students: apiFaculty.number_of_students || '-',
      programs: apiFaculty.programs || [],
      media: {
        images: apiFaculty.media_images || [],
        files: apiFaculty.media_files || []
      },
      content: {
        intro: apiFaculty.intros || [],
        advantages: apiFaculty.advantages || []
      }
    };
  },

  /**
   * 将后端专业数据转换为前端格式（简化版，用于列表展示）
   * @param {Object} apiProgram - 后端专业数据
   * @returns {Object} 前端专业数据格式
   */
  mapProgramFromApi: (apiProgram) => {
    return {
      id: apiProgram.id,
      name: apiProgram.name_cn || '',
      nameEn: apiProgram.name_en || '',
      level: apiProgram.academic_degree || '-',
      mode: apiProgram.mode || '课程型',
      duration: apiProgram.duration || '-',
      credits: apiProgram.credits || '-',
      tuitionFee: apiProgram.fees_tuition || '-',
      totalFee: apiProgram.fees_total || '-',
      currency: apiProgram.currency || 'MYR',
      interview: apiProgram.is_interview ? '是' : '否',
      portfolio: apiProgram.is_portfolio || false,
      crossMajor: apiProgram.is_interdisciplinary || false,
      intakes: apiProgram.intakes || '-',
      ranking: apiProgram.ranking_qs || '-',
      faculty: {
        id: apiProgram.faculty_id,
        name: apiProgram.faculty_name_cn || '',
        nameEn: apiProgram.faculty_name_en || ''
      },
      campus: {
        id: apiProgram.campus_id,
        name: apiProgram.campus_name_cn || '',
        nameEn: apiProgram.campus_name_en || ''
      }
    };
  }
};
